import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { isoToDisplayDate } from '@/pages/accounting/statements/statementMeta';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { NoOperationsCopy } from '../../recordMeta';

type NoOperationsProps = {
    copy: NoOperationsCopy;
    /** The date the jump moves to, or `null` when there is nowhere to go. */
    jumpTo: string | null;
    onJump: () => void;
};

/**
 * The picker's place when the date leaves nothing to pick.
 *
 * It **replaces** the list rather than sitting under it, because an empty grid
 * above a form is a screen that looks broken. What it says depends on which
 * empty this is — `noOperationsCopy` in `recordMeta` makes that call — and the
 * difference matters:
 *
 * - **Before every recipe started.** The date is the thing that is wrong, it can
 *   be named ("the earliest recipe starts 01/01/2026"), and the button fixes it
 *   in one press. That date always has at least one operation in force, so the
 *   jump cannot land on another empty list.
 * - **Nothing in force on a date that is not before everything.** A gap between
 *   versions, or every window has since expired. Same remedy, softer sentence.
 * - **Nothing configured for this session at all.** Not an error and not about
 *   the date — a role may permit nothing — so no jump is offered, and the copy
 *   points at the person who can change it.
 */
export const NoOperations: React.FC<NoOperationsProps> = ({
    copy,
    jumpTo,
    onJump,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('operations.record.picker.title')}
            className="flex flex-col items-start gap-2 rounded-2xl border border-dashed border-separator p-6"
            {...getNodeTestIdProp('operations-record-no-operations')}
        >
            <Icon
                aria-hidden
                className="text-2xl text-muted"
                icon="gravity-ui:calendar"
            />
            <span
                className="text-sm font-medium text-foreground"
                {...getNodeTestIdProp('operations-record-no-operations-title')}
            >
                {t(copy.titleKey)}
            </span>
            <p
                className="text-sm text-muted"
                {...getNodeTestIdProp('operations-record-no-operations-body')}
            >
                {t(copy.bodyKey, copy.bodyArgs)}
            </p>

            {jumpTo ? (
                <Button
                    className="mt-2"
                    onPress={onJump}
                    variant="secondary"
                    {...getNodeTestIdProp(
                        'operations-record-no-operations-jump',
                    )}
                >
                    {t('operations.record.noOperations.jump', {
                        date: isoToDisplayDate(jumpTo),
                    })}
                </Button>
            ) : null}
        </section>
    );
};
